import { STATUSPAGE } from '@repo/ui';
import {
  CourseUpdateUseCaseInterface,
  CourseCreateUseCaseInterface,
  CourseDeleteUseCaseInterface,
  CourseGetByIdUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

export const useCourse = () => {
  // States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [courseByIdSelected, setCourseByIdSelected] =
    useState<CourseGetByIdUseCaseInterface.Result>();
  //Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { courseService } = useService();
  const { coursesUser } = useAppSelector((state) => state.Courses);
  const [listStatus, setListStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [loadingCourseById, setLoadingCourseById] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );

  // Functions
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const location = useLocation();
  const courseId = location?.state?.id;

  // Helper functions
  const resetForm = () => {
    formik.resetForm();
    setCourseByIdSelected(null);
    clearNavigationState();
  };

  const clearNavigationState = () => {
    navigate(null, { state: null });
  };

  // Formik initialization
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      nome: courseByIdSelected?.nome || null,
    },
    validationSchema: Yup.object({
      nome: Yup.string().required('Campo é obrigatório'),
    }),
    onSubmit: (values) => {
      courseId
        ? requestCourseUpdate({ id: courseId, ...values })
        : requestCourseCreate(values);
    },
  });

  // list course
  const requestCourseList = useCallback(async () => {
    setListStatus(STATUSPAGE.LOADING);
    return courseService
      .courseList()
      .then((res) => {
        dispatch(loadCoursesUser(res));
      })
      .catch((err) => {
        notifyError(err || 'Um erro inesperado ocorreu, tente novamente.');
      })
      .finally(() => {
        setListStatus(STATUSPAGE.DONE);
      });
  }, []);

  const [loadingCourse, setLoadingCourse] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );

  // create couse
  const requestCourseCreate = useCallback(
    async (params: CourseCreateUseCaseInterface.Params) => {
      setLoadingCourse(STATUSPAGE.LOADING);
      return courseService
        .courseCreate(params)
        .then((res) => {
          notifySuccess(res.message || 'Curso criado com sucesso');
          requestCourseList();
          handleToggleModal();
        })

        .catch((err) => {
          notifyError(err.message || 'Erro ao criar curso');
        })
        .finally(() => {
          setLoadingCourse(STATUSPAGE.DONE);
        });
    },
    [handleToggleModal],
  );

  // delete course
  const requestCourseDelete = useCallback(
    async (params: CourseDeleteUseCaseInterface.Params) => {
      return courseService
        .courseDelete(params)
        .then((res) => {
          notifySuccess(res.message || 'Curso excluído com sucesso.');
          requestCourseList();
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao excluir um curso.');
        });
    },
    [],
  );

  // update course
  const requestCourseUpdate = useCallback(
    async (params: CourseUpdateUseCaseInterface.Params) => {
      setLoadingCourse(STATUSPAGE.LOADING);
      return courseService
        .courseUpdate(params)
        .then((res) => {
          notifySuccess(res.message || 'Curso atualizado com sucesso.');
          requestCourseList();
          handleToggleModal();
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao atualizar um curso.');
        })
        .finally(() => {
          setLoadingCourse(STATUSPAGE.DONE);
        });
    },
    [handleToggleModal],
  );

  // get course by id
  const requestCourseGetById = useCallback(
    async (params: CourseGetByIdUseCaseInterface.Params) => {
      setLoadingCourseById(STATUSPAGE.LOADING);
      return courseService
        .courseGetById(params)
        .then((res: CourseGetByIdUseCaseInterface.Result) => {
          setCourseByIdSelected(res);
        })
        .catch((err) => {
          notifyError(
            err.message || 'Um erro inesperado ocorreu, tente novamente.',
          );
        })
        .finally(() => {
          setLoadingCourseById(STATUSPAGE.DONE);
        });
    },
    [],
  );

  return {
    listStatus,
    requestCourseList,
    handleToggleModal,
    resetForm,
    requestCourseCreate,
    requestCourseDelete,
    requestCourseUpdate,
    requestCourseGetById,
    loadingCourse,
    formik,
    coursesUser,
    isModalOpen,
    courseByIdSelected,
    loadingCourseById,
  };
};
